/**
 * canExecuteNow — instantaneous "ready to launch" check for a contract.
 *
 * Returns whether the player can ACTUALLY execute the contract right now
 * with the resources available: at least one idle vehicle satisfying the
 * contract requirements AND one free driver qualified for the contract,
 * where the PAIR is itself compatible (driver holds the permit class of
 * that vehicle).
 *
 * 15/06/2026 — Refonte (retour utilisateur : « il est indiqué prêt mais
 * les deux conditions chauffeur + flotte ne sont pas respectées ») : la
 * vérification réutilise désormais les VRAIES règles d'assignation de
 * utils/contractCompatibility (équipement frigo/ADR, spécialités
 * chauffeur, permis hiérarchiques, zone frigo dépôt) et exige une paire
 * véhicule+chauffeur mutuellement compatible — exactement ce que la
 * modale d'assignation acceptera.
 *
 * Used by the Responsable d'exploitation feature (04/06/2026) — when this
 * support role is hired, every contract row gets a "🟢 Prêt" badge and
 * readiness filters/sorts.
 *
 * @param {Object} contract
 * @param {Array<Object>} vehicles
 * @param {Array<Object>} drivers
 * @param {Object} [opts] { activeInvestments, ownedDepots } — enables the
 *   depot cold-zone gate (frigo T3+) when provided.
 * @returns {{ ready:boolean, vehicleId:string|null, driverId:string|null, reason:string|null }}
 */
import {
  vehicleSatisfiesContract,
  driverSatisfiesContract,
  driverCanDriveVehicle,
  frigoZoneBlocked,
} from './contractCompatibility';

function isIdleVehicle(v) {
  return v && (v.status === 'idle' || v.status === undefined || v.status === null) && !v.assignedContractId;
}
function isIdleDriver(d) {
  // 'idle' or 'active' without an assignment is fine; 'training', 'sick',
  // 'vacation', 'returning' are NOT.
  // 06/02/2026 — P1 fix : un chauffeur revenant d'une mission ponctuelle
  // (status 'returning' posé par releaseDriversOnContractComplete) ne doit
  // pas être réassignable avant que son camion soit rentré.
  // 22/07/2026 — bug user prod (récurrent) : « bouton Accepter disponible
  // alors qu'aucun chauffeur n'est disponible ». Les statuts 'holiday'
  // (congés payés), 'striking' et le flag `onHoliday` n'étaient PAS
  // exclus ici alors que la modale d'assignation les refuse → bouton
  // actif mais modale vide. Alignement strict sur les règles de la
  // modale (ContractAssignModal.availableDrivers).
  if (!d) return false;
  if (d.status === 'training' || d.status === 'sick' || d.status === 'vacation'
    || d.status === 'holiday' || d.status === 'striking' || d.status === 'returning') return false;
  if (d.onHoliday) return false;
  return !d.assignedVehicleId;
}

export default function canExecuteNow(contract, vehicles = [], drivers = [], opts = {}) {
  if (!contract) return { ready: false, vehicleId: null, driverId: null, reason: 'no-contract' };

  // Depot cold-zone gate (frigo T3+) — same rule as the accept button.
  if (opts.activeInvestments && frigoZoneBlocked(contract, opts.activeInvestments, opts.ownedDepots || [])) {
    return { ready: false, vehicleId: null, driverId: null, reason: 'frigo-zone' };
  }

  const okVehicles = (vehicles || []).filter(v => isIdleVehicle(v) && vehicleSatisfiesContract(v, contract).ok);
  if (okVehicles.length === 0) {
    return { ready: false, vehicleId: null, driverId: null, reason: 'no-vehicle' };
  }
  const okDrivers = (drivers || []).filter(d => isIdleDriver(d) && driverSatisfiesContract(d, contract).ok);
  if (okDrivers.length === 0) {
    return { ready: false, vehicleId: okVehicles[0].id, driverId: null, reason: 'no-driver' };
  }
  // Cross-check: the pair must be mutually compatible (permit class).
  for (const v of okVehicles) {
    const d = okDrivers.find(dr => driverCanDriveVehicle(dr, v).ok);
    if (d) return { ready: true, vehicleId: v.id, driverId: d.id, reason: null };
  }
  return { ready: false, vehicleId: okVehicles[0].id, driverId: null, reason: 'no-compatible-pair' };
}

/**
 * Convenience boolean check.
 */
export function isContractReady(contract, vehicles, drivers, opts) {
  return canExecuteNow(contract, vehicles, drivers, opts).ready;
}

/**
 * 20/07/2026 — user prod : quand ACCEPTER est grisé, expliquer PRÉCISÉMENT
 * pourquoi (tooltip). Retourne une phrase FR, ou null si le contrat est prêt.
 */
export function explainContractBlock(contract, vehicles = [], drivers = [], opts = {}) {
  const res = canExecuteNow(contract, vehicles, drivers, opts);
  if (res.ready) return null;
  const tier = contract.tierMin || 1;
  switch (res.reason) {
    case 'frigo-zone':
      return 'Zone froide requise au dépôt pour exécuter un contrat frigo T3+ (investissement Dépôt).';
    case 'no-vehicle': {
      if ((vehicles || []).length === 0) return 'Aucun camion dans votre flotte.';
      const checks = vehicles.map(v => vehicleSatisfiesContract(v, contract));
      if (checks.some(c => c.ok)) return 'Tous vos camions compatibles sont occupés, en panne ou en maintenance.';
      const specFail = checks.find(c => c.reason === 'specialty' || c.reason === 'equipment');
      if (specFail) {
        const label = specFail.reason === 'specialty' ? contract.requiresSpecialty : contract.category;
        return `Camion T${tier}+ avec équipement « ${label} » requis — aucun dans votre flotte.`;
      }
      return `Camion T${tier}+ requis — aucun dans votre flotte.`;
    }
    case 'no-driver': {
      if ((drivers || []).length === 0) return 'Aucun chauffeur dans votre équipe.';
      const checks = drivers.map(d => driverSatisfiesContract(d, contract));
      if (checks.some(c => c.ok)) return 'Tous vos chauffeurs qualifiés sont occupés (mission, formation, congés ou maladie).';
      const licFail = checks.find(c => c.reason === 'license');
      const qualFail = checks.find(c => c.reason === 'driver_specialty');
      const parts = [];
      if (licFail) parts.push(`permis ${licFail.value}`);
      if (qualFail) parts.push(`certification ${qualFail.value}`);
      return parts.length > 0
        ? `Chauffeur avec ${parts.join(' + ')} requis — personne de qualifié dans votre équipe (pensez aux formations RH).`
        : 'Aucun chauffeur qualifié dans votre équipe.';
    }
    case 'no-compatible-pair':
      return 'Camion et chauffeur qualifiés trouvés, mais aucun binôme possible : le permis du chauffeur qualifié ne couvre pas le camion compatible.';
    default:
      return 'Conditions non réunies pour accepter ce contrat.';
  }
}
